import { computed, ref, provide, inject, watch } from "vue";
import { eventBus } from "@/eventBus";
import { resizeAllPlotlyCharts } from "@/utils/plotlyResizeAll";
import {
  SIDEBAR_COLLAPSED_PX,
  SIDEBAR_EXPANDED_PX,
  SIDEBAR_MAX_PX,
  SIDEBAR_MIN_PX,
  SIDEBAR_WIDTH_STORAGE_KEY,
} from "@app/config/navigation";

const SIDEBAR_KEY = Symbol("fd-sidebar");

function readStoredWidth() {
  try {
    const raw = localStorage.getItem(SIDEBAR_WIDTH_STORAGE_KEY);
    const parsed = parseInt(raw, 10);
    if (Number.isFinite(parsed) && parsed >= SIDEBAR_MIN_PX && parsed <= SIDEBAR_MAX_PX) {
      return parsed;
    }
  } catch {
    void 0;
  }
  return SIDEBAR_EXPANDED_PX;
}

function applyRailWidth(px) {
  if (typeof document === "undefined") return;
  document.documentElement.style.setProperty("--fd-rail-width", `${px}px`);
}

export function provideSidebar() {
  const collapsed = ref(false);
  const expandedWidth = ref(readStoredWidth());

  applyRailWidth(expandedWidth.value);

  function toggle() {
    collapsed.value = !collapsed.value;
    applyRailWidth(collapsed.value ? SIDEBAR_COLLAPSED_PX : expandedWidth.value);
    eventBus.emit("resizeChart");
    resizeAllPlotlyCharts();
  }

  function setCollapsed(value) {
    collapsed.value = Boolean(value);
    applyRailWidth(collapsed.value ? SIDEBAR_COLLAPSED_PX : expandedWidth.value);
  }

  function setExpandedWidth(px) {
    const clamped = Math.min(SIDEBAR_MAX_PX, Math.max(SIDEBAR_MIN_PX, Math.round(px)));
    expandedWidth.value = clamped;
    localStorage.setItem(SIDEBAR_WIDTH_STORAGE_KEY, String(clamped));
    if (!collapsed.value) {
      applyRailWidth(clamped);
    }
  }

  watch(collapsed, (isCollapsed) => {
    applyRailWidth(isCollapsed ? SIDEBAR_COLLAPSED_PX : expandedWidth.value);
  });

  const widthPx = computed(() =>
    collapsed.value ? SIDEBAR_COLLAPSED_PX : expandedWidth.value
  );

  const api = { collapsed, widthPx, expandedWidth, toggle, setCollapsed, setExpandedWidth };
  provide(SIDEBAR_KEY, api);
  return api;
}

export function useSidebar() {
  const api = inject(SIDEBAR_KEY);
  if (!api) {
    throw new Error("useSidebar() must be used inside AppShell");
  }
  return api;
}
